import { useEffect, useState, RefObject } from 'react';

interface UseElementVisibilityOptions {
  threshold?: number;
  rootMargin?: string;
}

export function useElementVisibility(
  elementRef: RefObject<HTMLElement>,
  options: UseElementVisibilityOptions = {}
): boolean {
  const [isVisible, setIsVisible] = useState(false);
  const { threshold = 0, rootMargin = '0px' } = options;

  useEffect(() => {
    const element = elementRef.current;
    if (!element) return;

    const observer = new IntersectionObserver(
      (entries) => {
        const [entry] = entries;
        setIsVisible(entry.isIntersecting);
      },
      {
        threshold,
        rootMargin,
      }
    );

    observer.observe(element);

    return () => {
      observer.unobserve(element);
    };
  }, [elementRef, threshold, rootMargin]);

  return isVisible;
}

// New hook for query selector-based visibility detection
export function useQuerySelectorVisibility(
  selector: string,
  options: UseElementVisibilityOptions = {}
): boolean {
  const [isVisible, setIsVisible] = useState(false);
  const { threshold = 0, rootMargin = '0px' } = options;

  useEffect(() => {
    const element = document.querySelector(selector);
    if (!element) return;

    const observer = new IntersectionObserver(
      (entries) => {
        const [entry] = entries;
        setIsVisible(entry.isIntersecting);
      },
      {
        threshold,
        rootMargin,
      }
    );

    observer.observe(element);

    return () => {
      observer.unobserve(element);
    };
  }, [selector, threshold, rootMargin]);

  return isVisible;
}

export function useMultipleElementsVisibility(
  elementRefs: RefObject<HTMLElement>[],
  options: UseElementVisibilityOptions = {}
): boolean[] {
  const [visibilityStates, setVisibilityStates] = useState<boolean[]>(
    new Array(elementRefs.length).fill(false)
  );

  useEffect(() => {
    const observers: IntersectionObserver[] = [];
    const { threshold = 0, rootMargin = '0px' } = options;

    elementRefs.forEach((ref, index) => {
      const element = ref.current;
      if (!element) return;

      const observer = new IntersectionObserver(
        (entries) => {
          const [entry] = entries;
          setVisibilityStates(prev => {
            const newStates = [...prev];
            newStates[index] = entry.isIntersecting;
            return newStates;
          });
        },
        {
          threshold,
          rootMargin,
        }
      );

      observer.observe(element);
      observers.push(observer);
    });

    return () => {
      observers.forEach(observer => observer.disconnect());
    };
  }, [elementRefs, options.threshold, options.rootMargin]);

  return visibilityStates;
}